import { inject, observer } from 'mobx-react';
import * as React from 'react';

import AnalyticsService from '../../../services/AnalyticsService';
import { RouteService } from '../../../services/RouteService';
import EInvoiceSuppliersDataStore from '../../../stores/data/EInvoiceSuppliersDataStore';
import { FeatureStore } from '../../../stores/FeatureStore';
import InvoiceNameStore from '../../../stores/InvoiceNameStore';
import InvoiceSettingsStore from '../../../stores/InvoiceSettingsStore';
import InvoiceSettingsViewStore from '../../../stores/view/InvoiceSettingsViewStore';
import { IModalStore } from '../../../stores/ModalStore';
import { InvoiceSettings } from '../../../stores/models/CustomerInvoicesSettings';
import { ISsoStore } from '../../../stores/SsoStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import TemplateBillingSettingsCard, { ITemplateBillingSettingsCardBill, ITemplateBillingSettingsCardProps } from '../../../templates/settings-card';

interface IInvoiceSettingsProps {
  invoiceSettingsStore?: InvoiceSettingsStore;
  invoiceSettingsViewStore?: InvoiceSettingsViewStore;
  invoiceNameStore?: InvoiceNameStore;
  ssoStore?: ISsoStore;
  translateStore?: ITranslateStore;
  routeService?: RouteService;
  analyticsService?: AnalyticsService;
  modalStore?: IModalStore;
  showInvoiceSettings?: boolean;
  featureStore?: FeatureStore;
  eInvoiceSuppliersDataStore?: EInvoiceSuppliersDataStore;
}

@inject(
  'invoiceSettingsStore',
  'invoiceSettingsViewStore',
  'invoiceNameStore',
  'ssoStore',
  'translateStore',
  'routeService',
  'analyticsService',
  'modalStore',
  'featureStore',
  'eInvoiceSuppliersDataStore'
)
@observer
export default class InvoiceSettingsCard extends React.Component<IInvoiceSettingsProps> {
  public async componentDidMount(): Promise<any> {
    await this.props.eInvoiceSuppliersDataStore.tryLoadEInvoiceSuppliers();
    await this.props.invoiceSettingsStore.tryLoadInvoiceSettings();
    if (this.props.showInvoiceSettings) {
      this.props.invoiceSettingsViewStore.setCardOpen(true);
    }
  }

  public render(): JSX.Element {
    const invoiceSettings = this.props.invoiceSettingsStore.customerInvoicesSettings;

    const translate = this.props.translateStore.translate;

    const props: ITemplateBillingSettingsCardProps = {
      id: 'invoice-settings-card',
      title: translate('invoices.invoice-settings.title'),
      titleDescription: translate('invoices.invoice-settings.title-description'),
      labels: {
        title: translate('invoices.invoice-settings.invoice-title'),
        delivery: translate('invoices.invoice-settings.delivery-channels')
      },
      bills: null,
      singleBillButton: null,
      singleBillAdvisoryNoticeContent: translate('invoices.invoice-settings.notice.unified-invoice-advisory-notice'),
      dataErrorNoticeContent: <span>{translate('invoices.invoice-settings.error-loading-ref-numbers')}</span>,
      noActiveReferenceNumbersNoticeContent: translate('invoices.invoice-settings.notice.no-active-reference-numbers'),
      dataState: invoiceSettings.uiState,
      onToggle: (id) => this.props.invoiceSettingsViewStore.setCardOpen(id !== null),
      isOpen: this.props.invoiceSettingsViewStore.isCardOpen
    };

    if (invoiceSettings.isFinished) {
      props.bills = this.getReferenceNumbersProps();
    }

    return <TemplateBillingSettingsCard {...props} />;
  }

  private getReferenceNumbersProps(): ITemplateBillingSettingsCardBill[] {
    return this.props.invoiceSettingsStore.activeInvoiceSettings.map((invoiceSettings) => this.getInvoiceSettingsProps(invoiceSettings));
  }

  private getInvoiceSettingsProps(referenceNumber: InvoiceSettings): ITemplateBillingSettingsCardBill {
    const translate = this.props.translateStore.translate;
    const invoiceNameStore = this.props.invoiceNameStore;

    return {
      icon: invoiceNameStore.invoiceIconName(referenceNumber.contentType),
      serviceTitle: invoiceNameStore.refNumberName(referenceNumber.contentType, referenceNumber.title, referenceNumber.source),
      referenceNumber: referenceNumber.refNumber,
      deliveryChannels: this.props.invoiceSettingsViewStore.toDeliveryChannels(referenceNumber),
      settingsButton: {
        text: translate(`invoices.invoice-settings.settings-button${referenceNumber.state === 'UPDATING' ? '.saving' : ''}`),
        onClick: () => {
          this.props.invoiceSettingsViewStore.activateInvoiceSettingsAndLoadEInvoiceSuppliers(referenceNumber);
          this.props.analyticsService.invoiceSettingsViewInvoiceSettings(referenceNumber.contentType, referenceNumber.refNumber);
        },
        disabled: referenceNumber.state === 'UPDATING'
      }
    };
  }
}
